import { useState, useEffect } from 'react';
import { DevicePhoneMobileIcon, BellAlertIcon } from '@heroicons/react/24/outline';
import { supabase } from '../../lib/supabase';
import { setupPushTables } from '../../lib/pushNotifications';
import { toast } from 'react-hot-toast';

interface MobileAppLinks {
  app_store_url: string;
  play_store_url: string;
}

export default function MobileAppSettingsForm() {
  const [links, setLinks] = useState<MobileAppLinks>({ app_store_url: '', play_store_url: '' });
  const [serviceAccountJson, setServiceAccountJson] = useState('');
  const [serviceAccountProjectId, setServiceAccountProjectId] = useState('');
  const [saving, setSaving] = useState(false);
  const [savingFcm, setSavingFcm] = useState(false);
  const [settingUp, setSettingUp] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLinks();
    loadFcmConfig();
  }, []);

  const loadLinks = async () => {
    try {
      const { data } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'mobile_app_links')
        .maybeSingle();

      if (data?.value) {
        setLinks(data.value as MobileAppLinks);
      }
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  };

  const loadFcmConfig = async () => {
    try {
      const { data } = await supabase
        .from('system_settings')
        .select('value')
        .eq('key', 'fcm_service_account')
        .maybeSingle();

      if (data?.value) {
        const sa = data.value as { project_id?: string };
        if (sa.project_id) {
          setServiceAccountProjectId(sa.project_id);
        }
      }
    } catch {
      // ignore
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: existing } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'mobile_app_links')
        .maybeSingle();

      if (existing) {
        await supabase
          .from('system_settings')
          .update({ value: links, updated_at: new Date().toISOString() })
          .eq('key', 'mobile_app_links');
      } else {
        await supabase
          .from('system_settings')
          .insert({
            key: 'mobile_app_links',
            value: links,
            category: 'mobile',
            description: 'Download links for the mobile application',
            is_public: true,
          });
      }

      toast.success('Mobile app links saved');
    } catch {
      toast.error('Failed to save mobile app links');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveFcm = async () => {
    if (!serviceAccountJson.trim()) {
      toast.error('Please paste your service account JSON');
      return;
    }
    let parsed: Record<string, unknown>;
    try {
      parsed = JSON.parse(serviceAccountJson);
    } catch {
      toast.error('Invalid JSON format. Please paste the full service account JSON file contents.');
      return;
    }
    if (!parsed.project_id || !parsed.private_key || !parsed.client_email) {
      toast.error('Invalid service account. Must contain project_id, private_key, and client_email.');
      return;
    }
    setSavingFcm(true);
    try {
      const { data: existing } = await supabase
        .from('system_settings')
        .select('id')
        .eq('key', 'fcm_service_account')
        .maybeSingle();

      if (existing) {
        await supabase
          .from('system_settings')
          .update({ value: parsed, updated_at: new Date().toISOString() })
          .eq('key', 'fcm_service_account');
      } else {
        await supabase
          .from('system_settings')
          .insert({
            key: 'fcm_service_account',
            value: parsed,
            category: 'mobile',
            description: 'Firebase service account JSON for FCM HTTP v1 API',
            is_public: false,
          });
      }

      setServiceAccountProjectId(parsed.project_id as string);
      setServiceAccountJson('');
      toast.success('Service account saved successfully');
    } catch {
      toast.error('Failed to save service account');
    } finally {
      setSavingFcm(false);
    }
  };

  const handleSetupTables = async () => {
    setSettingUp(true);
    try {
      const result = await setupPushTables();
      if (result.success) {
        toast.success('Push notification tables created successfully');
      } else {
        toast.error(result.error || 'Failed to setup tables');
      }
    } catch {
      toast.error('Failed to setup push notification tables');
    } finally {
      setSettingUp(false);
    }
  };

  if (loading) {
    return (
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-6 bg-gray-200 rounded w-1/3"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
          <div className="h-10 bg-gray-200 rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center space-x-3 mb-6">
        <div className="w-10 h-10 bg-golf-green/10 rounded-lg flex items-center justify-center">
          <DevicePhoneMobileIcon className="w-5 h-5 text-golf-green" />
        </div>
        <div>
          <h2 className="text-lg font-bold text-gray-900">Mobile App</h2>
          <p className="text-sm text-gray-500">Configure download links for the mobile application</p>
        </div>
      </div>

      <div className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Apple App Store URL
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.8-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M13 3.5c.73-.83 1.94-1.46 2.94-1.5.13 1.17-.34 2.35-1.04 3.19-.69.85-1.83 1.51-2.95 1.42-.15-1.15.41-2.35 1.05-3.11z"/>
              </svg>
            </div>
            <input
              type="url"
              value={links.app_store_url}
              onChange={(e) => setLinks({ ...links, app_store_url: e.target.value })}
              placeholder="https://apps.apple.com/app/..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-green/20 focus:border-golf-green text-sm"
            />
          </div>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            Google Play Store URL
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <svg className="w-5 h-5 text-gray-400" viewBox="0 0 24 24" fill="currentColor">
                <path d="M3.609 1.814L13.792 12 3.61 22.186a.996.996 0 0 1-.61-.92V2.734a1 1 0 0 1 .609-.92zm10.89 10.893l2.302 2.302-10.937 6.333 8.635-8.635zm3.199-3.198l2.807 1.626a1 1 0 0 1 0 1.73l-2.808 1.626L15.206 12l2.492-2.491zM5.864 2.658L16.802 8.99l-2.303 2.303-8.635-8.635z"/>
              </svg>
            </div>
            <input
              type="url"
              value={links.play_store_url}
              onChange={(e) => setLinks({ ...links, play_store_url: e.target.value })}
              placeholder="https://play.google.com/store/apps/details?id=..."
              className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-green/20 focus:border-golf-green text-sm"
            />
          </div>
        </div>

        <div className="pt-2">
          <button
            onClick={handleSave}
            disabled={saving}
            className="px-5 py-2.5 bg-golf-green text-white font-medium rounded-lg hover:bg-golf-green/90 transition-colors disabled:opacity-50"
          >
            {saving ? 'Saving...' : 'Save App Links'}
          </button>
        </div>
      </div>

      {/* FCM Configuration */}
      <div className="border-t border-gray-200 pt-6 mt-6">
        <div className="flex items-center space-x-3 mb-6">
          <div className="w-10 h-10 bg-orange-50 rounded-lg flex items-center justify-center">
            <BellAlertIcon className="w-5 h-5 text-orange-600" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-gray-900">Push Notifications</h3>
            <p className="text-sm text-gray-500">Configure Firebase Cloud Messaging (HTTP v1 API)</p>
          </div>
        </div>

        <div className="space-y-4">
          {serviceAccountProjectId && (
            <div className="flex items-center gap-2 px-3 py-2 bg-green-50 border border-green-200 rounded-lg">
              <div className="w-2 h-2 rounded-full bg-green-500"></div>
              <p className="text-sm text-green-700">
                Connected to project: <span className="font-semibold">{serviceAccountProjectId}</span>
              </p>
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Firebase Service Account JSON
            </label>
            <textarea
              value={serviceAccountJson}
              onChange={(e) => setServiceAccountJson(e.target.value)}
              placeholder='Paste your service account JSON here...&#10;&#10;{&#10;  "type": "service_account",&#10;  "project_id": "your-project-id",&#10;  ...&#10;}'
              rows={6}
              className="w-full px-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-golf-green/20 focus:border-golf-green text-xs font-mono resize-y"
            />
            <p className="text-xs text-gray-400 mt-1.5">
              Firebase Console &gt; Project Settings &gt; Service accounts &gt; Generate new private key
            </p>
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={handleSaveFcm}
              disabled={savingFcm || !serviceAccountJson.trim()}
              className="px-5 py-2.5 bg-golf-green text-white font-medium rounded-lg hover:bg-golf-green/90 transition-colors disabled:opacity-50"
            >
              {savingFcm ? 'Saving...' : 'Save Service Account'}
            </button>

            <button
              onClick={handleSetupTables}
              disabled={settingUp}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 font-medium rounded-lg hover:bg-gray-200 transition-colors disabled:opacity-50 text-sm"
            >
              {settingUp ? 'Setting up...' : 'Setup Push Tables'}
            </button>
          </div>

          <div className="bg-amber-50 rounded-lg p-3 border border-amber-100">
            <p className="text-xs text-amber-700 leading-relaxed">
              <strong>How to obtain a device token:</strong> Your native mobile app must integrate the Firebase Cloud Messaging SDK.
              On app startup, the SDK provides a registration token which is then sent to this system via
              <code className="bg-amber-100 px-1 rounded mx-0.5">window.registerPushToken(token, platform)</code>
              or by calling <code className="bg-amber-100 px-1 rounded mx-0.5">/functions/v1/send-push-notification</code>
              with <code className="bg-amber-100 px-1 rounded mx-0.5">action: "register_token"</code>.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
